import { cn } from "@/lib/utils";
import Link from "next/link";

type ButtonVariant = "primary" | "outline" | "ghost" | "green" | "soft";
type ButtonSize    = "sm" | "md" | "lg";

interface BaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: React.ReactNode;
  /** Analytics: GTM/Segment listeners can hook via [data-cta="..."] selectors. */
  "data-cta"?: string;
  /** Analytics: where on the page the CTA lives (e.g., "hero", "header", "cta-band"). */
  "data-cta-location"?: string;
}

interface ButtonAsButton extends BaseProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  href?: never;
}

interface ButtonAsLink extends BaseProps {
  href: string;
  target?: string;
  rel?: string;
}

type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-white border border-accent hover:brightness-[0.92] hover:-translate-y-px",
  outline:
    "border border-ink/[0.18] text-ink bg-transparent hover:border-ink/40 hover:bg-ink/[0.04]",
  ghost:
    "text-ink-2 hover:text-ink hover:bg-ink/[0.05]",
  green:
    "bg-good text-white font-medium hover:brightness-[0.92] hover:-translate-y-px border border-good",
  soft:
    "bg-accent/[0.08] text-accent border border-accent/30 hover:bg-accent/[0.14] hover:border-accent/50 hover:-translate-y-px active:translate-y-0",
};

/* Custom CSS classes that must NOT pass through twMerge (it strips unknown classes) */
const variantRawClasses: Partial<Record<ButtonVariant, string>> = {};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-[14px] py-[7px] text-[13px]",
  md: "px-[18px] py-[10px] text-[14px]",
  lg: "px-[24px] py-[13px] text-[15px]",
};

const base =
  "inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-md)] font-medium leading-none whitespace-nowrap transition-[transform,filter,background-color,border-color] duration-[180ms] ease-[cubic-bezier(0.2,0,0,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:pointer-events-none disabled:opacity-50";

export default function Button(props: ButtonProps) {
  const { variant = "primary", size = "md", className, children } = props;
  const raw = variantRawClasses[variant] ?? "";
  const classes = cn(base, variantStyles[variant], sizeStyles[size], className) + (raw ? ` ${raw}` : "");

  if ("href" in props && props.href) {
    const { href, target, rel, className: _c, variant: _v, size: _s, ...rest } = props;
    return (
      <Link href={href} target={target} rel={rel} className={classes} {...(rest as object)}>
        {children}
      </Link>
    );
  }

  const { href: _h, className: _c, variant: _v, size: _s, ...buttonProps } = props as ButtonAsButton & { href?: undefined };
  return (
    <button className={classes} {...buttonProps}>
      {children}
    </button>
  );
}
